/**
 * A module's client for its own BFF routes: same-origin, cookie-authed, JSON both ways, and a
 * non-2xx answer thrown as an `ApiError` carrying the server's message — which the modules
 * write to be read by the user. Each module makes one (`createApiClient('<module>')`) and keeps
 * its endpoints beside it in its own `web/api.ts`.
 */
import { notify } from './notify';
import { reportError } from './report-error';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The server's message out of an error body: `{ message }` or `{ error }` JSON, else the text. */
export function messageOf(body: string, fallback: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed !== null && typeof parsed === 'object') {
      const { message, error } = parsed as { message?: unknown; error?: unknown };
      if (typeof message === 'string') return message;
      if (typeof error === 'string') return error;
    }
  } catch {
    // Not JSON (a proxy's error page): the raw text is the best message available.
  }
  return body || fallback;
}

/** `scope` names the module in error reports. */
export function createApiClient(scope: string) {
  async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const res = await fetch(path, {
      method,
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!res.ok) {
      let text = '';
      try {
        text = await res.text();
      } catch (error) {
        reportError(scope, error, { status: res.status, path });
      }
      throw new ApiError(res.status, messageOf(text, res.statusText));
    }
    const text = await res.text();
    return (text === '' ? undefined : JSON.parse(text)) as T;
  }

  /** The message to show for a failed call: the server's own when there is one. */
  const errorMessage = (error: unknown, fallback = 'Something went wrong. Try again.'): string =>
    error instanceof ApiError ? error.message : fallback;

  /**
   * A refused user action: the error goes to the error report (the toast alone would drop it),
   * then the user sees the server's message or the fallback.
   */
  const fail = (error: unknown, fallback: string) => {
    reportError(scope, error, { action: fallback });
    notify.error(errorMessage(error, fallback));
  };

  return { request, errorMessage, fail };
}
